// ✅ src/pages/OrderManagement/EstimationListing.jsx
// Backend mount: app.use("/api/estimationListing", router)
// API used: GET /api/estimationListing/list?branchid=&page=&limit=&search=

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const EstimationListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const onRowClick = (item) => {
    navigate(
      `/OrderManagement/EstimationEntry?mode=view&projectid=${item.projectId}&estimationid=${item.estimationId || ""}`
    );
  };

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ branchid for params
  const branchid = Number(branchId || 0);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // table data
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totalEstimated: 0,
    totalOriginal: 0,
  });

  // loading + error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // search + pagination
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRows: 0,
    totalPages: 1,
  });

  const canPrev = pagination.page > 1;
  const canNext = pagination.page < pagination.totalPages;

  const fetchData = async () => {
    if (!branchid) {
      setError("branchId not found in localStorage");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${BASE_URL}/api/estimationListing/list`, {
        params: {
          branchid,
          page,
          limit,
          search,
        },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setRows([]);
        setTotals({ totalEstimated: 0, totalOriginal: 0 });
        setPagination({ page, limit, totalRows: 0, totalPages: 1 });
        setError(res.data?.message || "Failed to load");
        return;
      }

      setRows(res.data.data || []);
      setTotals(
        res.data.totals || { totalEstimated: 0, totalOriginal: 0 }
      );
      setPagination(
        res.data.pagination || { page, limit, totalRows: 0, totalPages: 1 }
      );
    } catch (e) {
      console.error(e);
      setError("Server error while loading estimation listing");
      setRows([]);
      setTotals({ totalEstimated: 0, totalOriginal: 0 });
      setPagination({ page, limit, totalRows: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid, page, limit, search, commonHeaders]);

  const handleAddNew = () => {
    navigate("/OrderManagement/EstimationEntry");
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchText.trim());
  };

  const handleClear = () => {
    setSearchText("");
    setSearch("");
    setPage(1);
  };

  const formatNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ESTIMATION LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
            <div className="flex items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Estimation Listing
              </h1>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex items-center gap-2">
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                    isDark
                      ? "border-white/20 bg-white/10"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  <FaSearch
                    className={isDark ? "text-white/80" : "text-gray-600"}
                  />
                  <input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Search Project Name..."
                    className={`outline-none bg-transparent w-64 ${
                      isDark
                        ? "text-white placeholder:text-white/60"
                        : "text-gray-900 placeholder:text-gray-500"
                    }`}
                  />
                </div>

                <button
                  onClick={handleSearch}
                  className={`px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${buttonClass} ${hoverClass} text-white`}
                >
                  Search
                </button>

                <button
                  onClick={handleClear}
                  className={`px-4 py-2 rounded-lg font-semibold border shadow-sm hover:shadow-md transition ${
                    isDark
                      ? "border-white/20 text-white"
                      : "border-gray-300 text-gray-800"
                  }`}
                >
                  Clear
                </button>
              </div>

              <button
                onClick={handleAddNew}
                className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {error ? (
            <div
              className={`mb-4 p-3 rounded-lg border ${
                isDark
                  ? "border-red-500/40 bg-red-500/10 text-red-200"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {error}
            </div>
          ) : null}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Project Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Project Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Estimated Value
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Original Value
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className={`px-4 py-6 text-center ${
                          isDark ? "text-white" : "text-gray-700"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className={`px-4 py-6 text-center ${
                          isDark ? "text-white" : "text-gray-700"
                        }`}
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    rows.map((item) => (
                      <tr
                        key={item.estimationId}
                        className={`transition-colors duration-200 ${
                          isDark
                            ? "bg-gray-800 hover:bg-gray-750"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.rowno}
                        </td>

                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-blue-400"
                              : "border-gray-200 text-blue-600"
                          }`}
                        >
                          <span
                            onClick={() => onRowClick(item)}
                            className="hover:underline cursor-pointer"
                          >
                            {item.projectName}
                          </span>
                        </td>

                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.projectCode}
                        </td>

                        <td
                          className={`px-4 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {formatNum(item.estimatedValue)}
                        </td>

                        <td
                          className={`px-4 py-3 text-center ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {formatNum(item.originalValue)}
                        </td>
                      </tr>
                    ))
                  )}

                  <tr
                    className={`font-semibold border-t-2 ${
                      isDark
                        ? "bg-gray-750 border-gray-600"
                        : "bg-gray-100 border-gray-300"
                    }`}
                  >
                    <td
                      className={`px-4 py-3 text-center border-r ${
                        isDark
                          ? "border-gray-700 text-white"
                          : "border-gray-200 text-gray-900"
                      }`}
                    ></td>
                    <td
                      className={`px-4 py-3 text-center border-r ${
                        isDark
                          ? "border-gray-700 text-white"
                          : "border-gray-200 text-gray-900"
                      }`}
                    ></td>
                    <td
                      className={`px-4 py-3 text-center border-r ${
                        isDark
                          ? "border-gray-700 text-white"
                          : "border-gray-200 text-gray-900"
                      }`}
                    ></td>

                    <td
                      className={`px-4 py-3 text-center border-r ${
                        isDark
                          ? "border-gray-700 text-white"
                          : "border-gray-200 text-gray-900"
                      }`}
                    >
                      {formatNum(totals.totalEstimated)}
                    </td>

                    <td
                      className={`px-4 py-3 text-center ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatNum(totals.totalOriginal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-5">
            <div className={isDark ? "text-white/80" : "text-gray-700"}>
              Total: <b>{pagination.totalRows}</b> rows • Page{" "}
              <b>{pagination.page}</b> / <b>{pagination.totalPages || 1}</b>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(Number(e.target.value));
                }}
                className={`px-3 py-2 rounded-lg border ${
                  isDark
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n} className="text-black">
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                disabled={!canPrev}
                onClick={() => canPrev && setPage((p) => p - 1)}
                className={`px-4 py-2 rounded-lg font-semibold border flex items-center gap-2 ${
                  canPrev
                    ? isDark
                      ? "border-white/20 text-white hover:bg-white/10"
                      : "border-gray-300 text-gray-800 hover:bg-gray-50"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <FaChevronLeft /> Prev
              </button>

              <button
                disabled={!canNext}
                onClick={() => canNext && setPage((p) => p + 1)}
                className={`px-4 py-2 rounded-lg font-semibold border flex items-center gap-2 ${
                  canNext
                    ? isDark
                      ? "border-white/20 text-white hover:bg-white/10"
                      : "border-gray-300 text-gray-800 hover:bg-gray-50"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                Next <FaChevronRight />
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default EstimationListing;